import { KontingentType } from './dispatch-planner-csv.service';
import { getPlannerTeamColor } from './dispatch-planner-teams';

const MARKER_PNG: Record<KontingentType, string> = {
  kueche: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png',
  moebel: 'https://maps.google.com/mapfiles/ms/icons/yellow-dot.png',
  other: 'https://maps.google.com/mapfiles/ms/icons/grey-dot.png'
};

export function getDefaultMarkerIconUrl(kontingent: KontingentType, selected = false): string {
  if (selected) {
    return buildDispatchMarkerIcon('', kontingent, true, true, undefined, false);
  }
  return MARKER_PNG[kontingent] || MARKER_PNG.other;
}

function buildMarkerSelectionRings(focused: boolean, duplicate: boolean): string {
  const rings: string[] = [];

  if (focused) {
    rings.push(
      `<circle cx="21" cy="17" r="18" fill="none" stroke="#2c3e50" stroke-width="3.5"/>`,
      `<circle cx="21" cy="17" r="15.5" fill="none" stroke="#ffffff" stroke-width="2"/>`
    );
  }

  if (duplicate) {
    rings.push(
      `<circle cx="21" cy="17" r="15" fill="none" stroke="#e74c3c" stroke-width="3" stroke-dasharray="4 3"/>`
    );
  }

  return rings.join('\n  ');
}

const SELECTED_MARKER_FILL = '#ff4081';
const SELECTED_MARKER_STROKE = '#c2185b';

export function buildDispatchMarkerIcon(
  label: string,
  kontingent: KontingentType,
  muted = false,
  selected = false,
  teamIndex?: number,
  duplicate = false,
  focused = false
): string {
  const colors: Record<KontingentType, { fill: string; stroke: string }> = {
    kueche: { fill: '#27ae60', stroke: '#1e8449' },
    moebel: { fill: '#f1c40f', stroke: '#d4ac0d' },
    other: { fill: '#7f8c8d', stroke: '#566566' }
  };
  const teamPalette = teamIndex != null && !muted && !selected ? getPlannerTeamColor(teamIndex) : null;
  const base = colors[kontingent] || colors.other;
  const fill = selected ? SELECTED_MARKER_FILL : teamPalette ? teamPalette.fill : base.fill;
  const stroke = selected ? SELECTED_MARKER_STROKE : teamPalette ? teamPalette.stroke : base.stroke;
  const effectiveStroke = focused && !selected ? '#2c3e50' : stroke;
  const effectiveStrokeWidth = focused && !selected ? '3.2' : '2';
  const labelColor = selected ? '#ffffff' : teamPalette ? teamPalette.fill : effectiveStroke;
  const innerCircleFill = selected ? '#ffe4ef' : '#ffffff';
  const safeLabel = String(label).slice(0, 3);
  const labelSvg = safeLabel
    ? `<text x="21" y="21" text-anchor="middle" font-family="Arial,sans-serif" font-size="12" font-weight="700" fill="${labelColor}">${safeLabel}</text>`
    : '';
  const pinOpacity = muted && !selected ? '0.55' : '1';
  const selectionRing = buildMarkerSelectionRings(focused, duplicate);

  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="42" height="54" viewBox="0 0 42 54">
  ${selectionRing}
  <path d="M21 0C11.6 0 4 7.6 4 17c0 12.8 17 37 17 37s17-24.2 17-37C38 7.6 30.4 0 21 0z" fill="${fill}" stroke="${effectiveStroke}" stroke-width="${effectiveStrokeWidth}" opacity="${pinOpacity}"/>
  <circle cx="21" cy="17" r="11" fill="${innerCircleFill}"/>
  ${labelSvg}
</svg>`;

  return `data:image/svg+xml;utf8,${encodeURIComponent(svg.trim())}`;
}

/** Filial-Marker (Start/Ende der Tour). */
export function buildBranchMarkerIcon(): string {
  const fill = '#1a5276';
  const stroke = '#154360';
  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="42" height="54" viewBox="0 0 42 54">
  <path d="M21 0C11.6 0 4 7.6 4 17c0 12.8 17 37 17 37s17-24.2 17-37C38 7.6 30.4 0 21 0z" fill="${fill}" stroke="${stroke}" stroke-width="2"/>
  <circle cx="21" cy="17" r="11" fill="#ffffff"/>
  <path d="M14 21h14v2H14zM16 14h10v9H16z" fill="${fill}"/>
  <rect x="19" y="18" width="4" height="5" fill="#ffffff"/>
</svg>`;

  return `data:image/svg+xml;utf8,${encodeURIComponent(svg.trim())}`;
}

/** Liefervorschlag (noch nicht disponiert) – grauer Marker mit Auftragsart (M, MR, O). */
export function buildDeliverySuggestionMarkerIcon(
  auftragsart: string,
  selected = false,
  duplicate = false,
  focused = false
): string {
  const fill = selected ? SELECTED_MARKER_FILL : '#95a5a6';
  const stroke = selected ? SELECTED_MARKER_STROKE : '#7f8c8d';
  const effectiveStroke = focused && !selected ? '#2c3e50' : stroke;
  const effectiveStrokeWidth = focused && !selected ? '3.2' : '2';
  const label = (auftragsart || 'M').trim().toUpperCase().slice(0, 3);
  const fontSize = label.length > 1 ? '9' : '11';
  const labelColor = selected ? '#ffffff' : stroke;
  const innerCircleFill = selected ? '#ffe4ef' : '#ffffff';
  const selectionRing = buildMarkerSelectionRings(focused, duplicate);
  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="42" height="54" viewBox="0 0 42 54">
  ${selectionRing}
  <path d="M21 0C11.6 0 4 7.6 4 17c0 12.8 17 37 17 37s17-24.2 17-37C38 7.6 30.4 0 21 0z" fill="${fill}" stroke="${effectiveStroke}" stroke-width="${effectiveStrokeWidth}"/>
  <circle cx="21" cy="17" r="11" fill="${innerCircleFill}"/>
  <text x="21" y="21" text-anchor="middle" font-family="Arial,sans-serif" font-size="${fontSize}" font-weight="700" fill="${labelColor}">${label}</text>
</svg>`;

  return `data:image/svg+xml;utf8,${encodeURIComponent(svg.trim())}`;
}

/** Kalender-Termin (fix gebucht) – teamfarbig mit Kalender-Symbol. */
export function buildCalendarAppointmentMarkerIcon(teamIndex: number): string {
  const palette = getPlannerTeamColor(teamIndex);
  const stripe = `${palette.css}59`;
  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="42" height="54" viewBox="0 0 42 54">
  <defs>
    <pattern id="calStripe${teamIndex}" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
      <rect width="6" height="6" fill="#ffffff"/>
      <line x1="0" y1="0" x2="0" y2="6" stroke="${stripe}" stroke-width="3"/>
    </pattern>
  </defs>
  <path d="M21 0C11.6 0 4 7.6 4 17c0 12.8 17 37 17 37s17-24.2 17-37C38 7.6 30.4 0 21 0z" fill="url(#calStripe${teamIndex})" stroke="${palette.stroke}" stroke-width="2.5"/>
  <circle cx="21" cy="17" r="11" fill="#ffffff" stroke="${palette.fill}" stroke-width="1.5"/>
  <rect x="14" y="12" width="14" height="12" rx="1.5" fill="${palette.fill}"/>
  <rect x="14" y="12" width="14" height="4" fill="${palette.stroke}"/>
  <line x1="17" y1="10" x2="17" y2="14" stroke="${palette.stroke}" stroke-width="1.5" stroke-linecap="round"/>
  <line x1="25" y1="10" x2="25" y2="14" stroke="${palette.stroke}" stroke-width="1.5" stroke-linecap="round"/>
</svg>`;

  return `data:image/svg+xml;utf8,${encodeURIComponent(svg.trim())}`;
}
